import { IcoEye16 } from '@onefootprint/icons';
import { LoadingSpinner } from '@onefootprint/ui';
import { useState } from 'react';
import { extractToolResultText } from '../../utils/tool-result';
import IdBadge from '../id-badge';
type MetaDecryptVaultCallProps = {
  input: Record<string, unknown>;
  toolResult?: string;
  defaultExpanded?: boolean;
  /** Suppress the leading icon when rendered on a tool-call timeline rail (the rail owns it). */
  hideIcon?: boolean;
};

/** Pull a human-readable name out of the decrypted vault so we can show "Viewed Nordic LLC"
 * instead of a raw fp_bid. Falls back to undefined when no name field was decrypted. */
const getEntityName = (decrypted: Record<string, unknown>): string | undefined => {
  const str = (k: string) => typeof decrypted[k] === 'string' ? (decrypted[k] as string).trim() : '';
  const business = str('business.name') || str('business.dba');
  if (business) return business;
  const person = [str('id.first_name'), str('id.last_name')].filter(Boolean).join(' ');
  return person || undefined;
};
const MetaDecryptVaultCall = ({
  input,
  toolResult,
  defaultExpanded = false,
  hideIcon
}: MetaDecryptVaultCallProps) => {
  const fpId = input.fp_id as string ?? '';
  const [expanded, setExpanded] = useState(defaultExpanded);
  if (!toolResult) {
    return <div className="flex items-start gap-2 text-caption-1 text-tertiary py-1">
        {!hideIcon && <LoadingSpinner size="default" color="tertiary" className="shrink-0 mt-px" />}
        <span className="inline-flex items-center gap-1">
          Viewing vault {fpId && <IdBadge id={fpId} size="compact" />}
        </span>
      </div>;
  }
  const text = extractToolResultText(toolResult);
  let decrypted: Record<string, unknown> | null = null;
  if (text) {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        decrypted = parsed;
      }
    } catch {
      // ignore
    }
  }
  const keys = decrypted ? Object.keys(decrypted).sort() : [];
  const entityName = decrypted ? getEntityName(decrypted) : undefined;
  return <div className="flex flex-col gap-1 py-1 text-caption-1">
      <button type="button" onClick={() => setExpanded(e => !e)} className="flex items-start gap-2 text-left cursor-pointer hover:text-secondary">
        {!hideIcon && <span className="shrink-0 flex items-center h-6">
            <IcoEye16 />
          </span>}
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-secondary inline-flex items-center gap-1">
            {entityName ? <>
                Viewed <span className="text-primary font-medium">{entityName}</span>
              </> : <>Viewed {fpId && <IdBadge id={fpId} size="compact" />}</>}
          </span>
        </div>
      </button>
      {expanded && decrypted &&
    // On a phone a long vault key leaves no room beside it, so each value drops under its key and wraps.
    <div className="ml-6 flex flex-col gap-1.5 md:gap-0.5 border-l border-tertiary pl-3 py-1">
          {keys.map(k => <div key={k} className="flex flex-col md:flex-row md:gap-2 min-w-0">
              <span className="font-mono text-tertiary shrink-0 max-md:break-all">{k}</span>
              <span className="font-mono text-secondary truncate max-md:whitespace-normal max-md:break-all">
                {decrypted[k] == null ? '—' : String(decrypted[k])}
              </span>
            </div>)}
        </div>}
    </div>;
};
export default MetaDecryptVaultCall;